import { useCallback, useEffect, useRef, useState } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import type {
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  IMicrophoneAudioTrack,
} from "agora-rtc-sdk-ng";
import { getRoomRtcToken } from "rc-app/api/guidedRoomActionsApi";
import {
  agoraAppIdFrom,
  canPublishAudio,
  msUntilRefresh,
  speakingFrom,
  connectionFrom,
  voiceErrorMessage,
  type VoiceConnection,
  type RtcGrant,
} from "rc-app/utils/guidedRoomVoice";

/* ── Voice for a guided tour room ──────────────────────────────────────
   Audio only, deliberately. A guided tour is a walkthrough with a voice
   over it — the thing everyone looks at is the property, not each other's
   faces, and video would compete with it for both attention and
   bandwidth. The dock's camera button stays off until there's a reason
   for it beyond "Agora supports it".

   ── HOW A PARTICIPANT BECOMES AUDIBLE ──
   The credential (session_id + session_secret) buys a token from
   rtc_token, and everything else — channel, uid, publish rights — comes
   back with it rather than being chosen here. That matters: the channel
   is derived from the room, so two clients cannot end up in different
   channels for the same room, and the role is derived from the presence
   record, so a guest cannot talk their way into publishing by editing
   anything client-side.

   ── UID IS A STRING ──
   The backend issues "VRS1189", not a number. The v4 SDK takes a string
   uid directly, and the token is signed for that exact string, so it must
   be passed through untouched. Coercing it to a number produces a token
   mismatch that surfaces as a generic auth failure.

   ── WHY NOT agora-react-uikit ──
   It's in package.json and it renders a whole video-call UI. The host
   page already has its own dock, its own people sheet and its own visual
   language; the uikit would bring a second one. We use the raw client and
   let the existing design stay in charge. */

export type VoiceStatus = "idle" | "connecting" | "live" | "error";

export type GuidedRoomVoice = {
  status: VoiceStatus;
  /** Publisher and actually unmuted. */
  micOn: boolean;
  /** False for guests — the dock hides the mic rather than teasing it. */
  canPublish: boolean;
  /** uid → is talking right now. Includes our own uid on purpose. */
  speaking: Record<string, boolean>;
  /** How many other people are audible. */
  remoteCount: number;
  /** Whether this browser is still IN the channel. `status` only records
      how the join went; this records whether it is still true. See the
      connection-state listener for why the difference matters. */
  connection: VoiceConnection;
  error: string;
  /** Set when the channel is live but this browser could not get a
      microphone. Listening is unaffected; only publishing is lost. */
  micError: string;
  toggleMic: () => Promise<void>;
  /* ── THE JOINED CLIENT, DELIBERATELY EXPOSED ──
     Chat rides this same client's data stream (utils/guidedRoomChat.ts
     explains why it can't have a channel of its own). Handed out as
     STATE rather than a ref so a consumer re-renders when the channel
     becomes usable — a ref would have useGuidedRoomChat attaching its
     listener to whatever happened to be there on first render, which is
     null. Null until joined, and null again the moment teardown starts. */
  client: IAgoraRTCClient | null;
};

type Credential = { session_id: string; session_secret: string } | null | undefined;

const IDLE: GuidedRoomVoice = {
  status: "idle",
  micOn: false,
  canPublish: false,
  speaking: {},
  remoteCount: 0,
  connection: "connected",
  error: "",
  micError: "",
  toggleMic: async () => {},
  client: null,
};

export const useGuidedRoomVoice = (
  roomId: string,
  credential: Credential,
  enabled = true,
): GuidedRoomVoice => {
  const [status, setStatus] = useState<VoiceStatus>("idle");
  const [connection, setConnection] = useState<VoiceConnection>("connected");
  const [micOn, setMicOn] = useState(false);
  const [canPublish, setCanPublish] = useState(false);
  const [speaking, setSpeaking] = useState<Record<string, boolean>>({});
  const [remoteCount, setRemoteCount] = useState(0);
  const [error, setError] = useState("");
  const [micError, setMicError] = useState("");
  const [liveClient, setLiveClient] = useState<IAgoraRTCClient | null>(null);

  const clientRef = useRef<IAgoraRTCClient | null>(null);
  const micRef = useRef<IMicrophoneAudioTrack | null>(null);
  const refreshRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /* Set the moment teardown begins. Every await below re-checks it, so a
     join that is still in flight when the host closes the tab cannot
     resurrect a client we already left. Without this, React 18's
     double-invoked effects in development join twice and leave a ghost
     participant in the channel. */
  const deadRef = useRef(false);

  const sid = credential?.session_id;
  const secret = credential?.session_secret;

  /* Buy a token. Split out because both the initial join and every
     renewal need one, and they must ask the same way. */
  const fetchGrant = useCallback(async (): Promise<RtcGrant> => {
    const g: any = await getRoomRtcToken(roomId, {
      session_id: String(sid),
      session_secret: String(secret),
    });
    if (!g?.token || !g?.channel || !g?.uid) {
      throw new Error("rtc_token returned no usable grant");
    }
    return g as RtcGrant;
  }, [roomId, sid, secret]);

  /* Renew ahead of expiry and reschedule from the NEW token's own expiry,
     so the chain never drifts. A failed renewal is logged and retried by
     Agora's own will-expire event rather than being escalated: the
     current token is still valid for a while, and tearing the room down
     because a refresh 404'd would be a worse outcome than a silent
     retry. */
  const scheduleRefresh = useCallback((grant: RtcGrant) => {
    if (refreshRef.current) clearTimeout(refreshRef.current);
    const wait = msUntilRefresh(grant);
    if (wait == null) return;
    refreshRef.current = setTimeout(async () => {
      if (deadRef.current || !clientRef.current) return;
      try {
        const next = await fetchGrant();
        if (deadRef.current || !clientRef.current) return;
        await clientRef.current.renewToken(next.token);
        scheduleRefresh(next);
      } catch {
        /* Leave it to token-privilege-will-expire below. */
      }
    }, wait);
  }, [fetchGrant]);

  useEffect(() => {
    if (!enabled || !roomId || !sid || !secret) return undefined;

    deadRef.current = false;
    let client: IAgoraRTCClient | null = null;

    (async () => {
      setStatus("connecting");
      setError("");
      setMicError("");
      try {
        const grant = await fetchGrant();
        if (deadRef.current) return;

        const appId = agoraAppIdFrom(grant.token, process.env.REACT_APP_AGORA_APP_ID);
        if (!appId) {
          setStatus("error");
          setError("Voice isn't configured for this environment.");
          return;
        }

        const publisher = canPublishAudio(grant);
        setCanPublish(publisher);

        client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
        clientRef.current = client;

        /* Someone started talking. Audio plays itself — there's no video
           element to attach it to, and the SDK handles output routing. */
        client.on("user-published", async (user: IAgoraRTCRemoteUser, media) => {
          if (media !== "audio" || deadRef.current || !client) return;
          try {
            await client.subscribe(user, "audio");
            user.audioTrack?.play();
            setRemoteCount(client.remoteUsers.length);
          } catch { /* one participant failing to subscribe isn't fatal */ }
        });
        client.on("user-unpublished", () => {
          if (!deadRef.current && client) setRemoteCount(client.remoteUsers.length);
        });
        client.on("user-left", () => {
          if (!deadRef.current && client) setRemoteCount(client.remoteUsers.length);
        });

        /* ── IS THIS PERSON STILL IN THE ROOM ──
           Nothing watched this until 2026-09-10, so `status` went "live"
           on join and stayed there for the rest of the tour. Take a
           buyer's wifi away mid-presentation and their screen still reads
           LIVE while voice is dead, chat cannot send and the walkthrough
           has stopped following the host. Tested by pulling the network
           for 25 seconds: 42 console errors, and not one word on screen.

           The buyer assumes the advisor went quiet. The advisor has no
           idea the buyer left. Both keep talking to nobody, which is the
           worst failure this feature has because neither party can even
           diagnose it.

           Agora reconnects on its own and recovers cleanly — verified,
           chat worked the instant the network returned — so this is not
           about repair. It is about saying what is happening while it
           happens. */
        client.on("connection-state-change", (curState: string) => {
          if (deadRef.current) return;
          setConnection(connectionFrom(curState));
        });

        /* Who's talking, for the rings in the people sheet. */
        client.enableAudioVolumeIndicator();
        client.on("volume-indicator", (volumes: any[]) => {
          if (!deadRef.current) setSpeaking(speakingFrom(volumes));
        });

        /* Agora's own backstop, 30s out. scheduleRefresh normally beats
           it; this catches the case where the timer was throttled by a
           backgrounded tab, which browsers do aggressively. */
        client.on("token-privilege-will-expire", async () => {
          if (deadRef.current || !client) return;
          try {
            const next = await fetchGrant();
            if (deadRef.current || !client) return;
            await client.renewToken(next.token);
            scheduleRefresh(next);
          } catch { /* nothing further to try — the token will lapse */ }
        });

        /* uid passed through exactly as issued — see the note above. */
        await client.join(appId, grant.channel, grant.token, grant.uid);
        if (deadRef.current) return;

        /* Join muted. A tour host opening the room is often mid-sentence
           with someone else, and an unrequested hot mic is the kind of
           mistake that gets a product distrusted.

           ── NEVER PUBLISH A DISABLED TRACK ──
           This used to disable the track and then publish it, to make the
           first unmute instant. Agora refuses that outright —
           `publish` throws TRACK_IS_DISABLED ("can not publish a disabled
           track") — and the throw landed in the mic catch below, so EVERY
           publisher's mic silently failed: the host's unmute did nothing
           and every lead's dock read "Couldn't start voice". Found
           end-to-end on 2026-09-11 with eight people in the room and not
           one able to speak.

           So the track is acquired here (that is where permission and
           device failures belong) and left disabled, which also switches
           the mic hardware off. It is published on the first unmute —
           see toggleMic. */
        /* ── A MIC FAILURE IS NOT A VOICE FAILURE ──
           Its own try/catch, because the two halves of "voice" fail
           independently and only one of them matters to most buyers.

           By this line the channel is joined and every remote participant
           is already audible — the tour WORKS. Acquiring a microphone can
           still fail for reasons that have nothing to do with the room: a
           permission prompt nobody answers (Agora gives up after 60s), a
           denied prompt, no input device, another app holding it.

           An earlier version let that throw into the outer catch, which
           set status "error" and told a buyer who was listening happily
           to "check your connection and try again". They could hear the
           advisor the whole time. Now the failure is recorded against the
           MIC alone: the room stays live, and the dock disables its own
           button with the real reason. */
        if (publisher) {
          try {
            const mic = await AgoraRTC.createMicrophoneAudioTrack();
            if (deadRef.current) { mic.close(); return; }
            await mic.setEnabled(false);
            micRef.current = mic;
          } catch (micErr: any) {
            if (deadRef.current) return;
            setMicError(voiceErrorMessage(micErr));
          }
        }

        if (deadRef.current) return;
        scheduleRefresh(grant);
        /* After join, never before: sending on an unjoined client throws
           "can not send data stream, not joined". */
        setLiveClient(client);
        setStatus("live");
      } catch (e: any) {
        if (deadRef.current) return;
        setStatus("error");
        setError(voiceErrorMessage(e));
      }
    })();

    return () => {
      deadRef.current = true;
      if (refreshRef.current) clearTimeout(refreshRef.current);
      refreshRef.current = null;
      const mic = micRef.current;
      const c = clientRef.current;
      micRef.current = null;
      clientRef.current = null;
      /* Close the track before leaving so the browser's mic indicator
         goes out immediately rather than whenever GC gets to it — people
         reasonably read a lingering red dot as "still being recorded". */
      try { mic?.close(); } catch { /* already gone */ }
      c?.leave().catch(() => { /* tab is closing; nothing to salvage */ });
      setLiveClient(null);
      setStatus("idle");
      setMicOn(false);
      setSpeaking({});
      setRemoteCount(0);
    };
  }, [enabled, roomId, sid, secret, fetchGrant, scheduleRefresh]);

  const toggleMic = useCallback(async () => {
    const mic = micRef.current;
    const client = clientRef.current;
    if (!mic || !client) return;
    const next = !micOn;
    try {
      await mic.setEnabled(next);
      /* Published on unmute, never before — Agora refuses to publish a
         disabled track (see the join note). Checked against localTracks
         every time rather than a flag of our own, because the SDK may
         drop a track it was told to disable; this stays right whichever
         way it behaves. */
      if (next && !client.localTracks.includes(mic)) {
        try {
          await client.publish([mic]);
        } catch (pubErr) {
          /* Never leave a live mic that nobody can hear — the browser's
             red dot would say "recording" while the room hears nothing. */
          try { await mic.setEnabled(false); } catch { /* already off */ }
          throw pubErr;
        }
      }
      setMicOn(next);
      /* Clear our own ring on mute rather than waiting for the next
         volume tick, which can be up to two seconds out and reads as the
         button not having worked. */
      if (!next) setSpeaking((prev) => ({ ...prev }));
    } catch (e: any) {
      setError(voiceErrorMessage(e));
    }
  }, [micOn]);

  if (!enabled) return IDLE;
  return { status, micOn, canPublish, speaking, remoteCount, connection, error, micError, toggleMic, client: liveClient };
};

export default useGuidedRoomVoice;
